import { useEffect } from "react";

export interface DynamicSeo {
  title: string;
  description?: string;
  canonical?: string;
  og?: Record<string, string>;
  jsonLd?: unknown;
}

/**
 * Client-side <head> enhancement for dynamic, non-prerendered routes (the
 * public timetable pages). Sets the document title/description and appends
 * canonical + OG + JSON-LD tags (marked `data-dynseo`), then cleans everything
 * up on unmount so nothing bleeds into the next route.
 *
 * This helps JS-rendering crawlers (Googlebot renders the page). Full
 * server-rendering for non-JS social scrapers is a separate, deferred step.
 */
export function useDynamicSeo(seo: DynamicSeo | null | undefined): void {
  const key = seo ? JSON.stringify(seo) : "";
  useEffect(() => {
    if (!seo) return;
    const head = document.head;
    const prevTitle = document.title;
    const descEl = head.querySelector<HTMLMetaElement>('meta[name="description"]');
    const prevDesc = descEl?.getAttribute("content") ?? null;
    const added: HTMLElement[] = [];

    document.title = seo.title;
    if (seo.description && descEl) descEl.setAttribute("content", seo.description);

    const add = (el: HTMLElement) => {
      el.setAttribute("data-dynseo", "");
      head.appendChild(el);
      added.push(el);
    };
    if (seo.canonical) {
      const l = document.createElement("link");
      l.setAttribute("rel", "canonical");
      l.setAttribute("href", seo.canonical);
      add(l);
    }
    for (const [k, v] of Object.entries(seo.og ?? {})) {
      const m = document.createElement("meta");
      m.setAttribute("property", k.startsWith("og:") ? k : `og:${k}`);
      m.setAttribute("content", v);
      add(m);
    }
    if (seo.jsonLd) {
      const s = document.createElement("script");
      s.setAttribute("type", "application/ld+json");
      s.textContent = JSON.stringify(seo.jsonLd);
      add(s);
    }

    return () => {
      document.title = prevTitle;
      if (descEl && prevDesc !== null) descEl.setAttribute("content", prevDesc);
      added.forEach((el) => el.remove());
    };
    // key already captures every field of `seo`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
